import type {
  RawOrderType,
  RawPlantListResponse,
  RawPlantNode,
  RawSubscriptionCode,
  RawSubscriptionStatus,
} from '@heviton/heviton-api-v2';
import type {
  OrderType,
  SubscriptionListItem,
  SubscriptionStatus,
  SubscriptionTypeCode,
} from '@/shared/data-types';
import { fromRawPlant } from './plant';

// BE `RawPlantNode`(+ optional subscription) → FE `SubscriptionListItem`(=`Plant` + 구독). **순수 변환** — 검증·throw 없음.
// 발전소 기본 정보는 `fromRawPlant`(plant 매퍼) 합성. 코드 union 은 소문자 FE 어휘 미러.
// 구독 여부 판정 축 = `subscriptionStatus`(BE 지침) — 미구독(INACTIVE)·code 부재(NULL)·NONE 은 미구독.

const STATUS_MAP: Record<RawSubscriptionStatus, SubscriptionStatus> = {
  INACTIVE: 'inactive',
  ACTIVE: 'active',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  SUSPENDED: 'suspended',
};

// NONE 은 매핑 테이블에서 제외 — 호출 전 걸러진다(미구독).
const TYPE_CODE_MAP: Record<Exclude<RawSubscriptionCode, 'NONE'>, SubscriptionTypeCode> = {
  MON: 'mon',
  PUB1Y: 'pub1y',
  PRE5Y: 'pre5y',
  TRIAL: 'trial',
};

const ORDER_TYPE_MAP: Record<RawOrderType, OrderType> = {
  MANUAL: 'manual',
  NORMAL: 'normal',
  BILLING: 'billing',
};

/** 발전소 1건 → 구독 목록 행. `fromRawPlant`(발전소 기본) 합성 + 구독 노드(없거나 `NONE` 이면 미구독) + 주문 노드. */
export function fromRawSubscriptionListItem(raw: RawPlantNode): SubscriptionListItem {
  const sub = raw.subscription;
  const code = sub?.subscriptionCode;
  // 구독 판정 = subscriptionStatus (미구독=INACTIVE). code 는 미구독이면 NULL 이라 축으로 못 씀.
  const subscribed = !!sub && !!code && code !== 'NONE' && sub.subscriptionStatus !== 'INACTIVE';
  // 주문 노드는 **구독중(ACTIVE)이어도 없을 수 있다** — 관리자가 월 구독을 수동으로 활성화했지만 아직
  // 주문 내역과 연동되지 않은 상태가 존재한다(결제 흐름을 거치지 않고 상태만 ACTIVE 가 된 케이스).
  // 그래서 "구독중이면 주문도 있다"를 전제로 삼으면 안 된다 — 소비처는 `order` 부재를 정상 경로로 다뤄야 하고,
  // 실제로 결제수단 변경(`isPaymentChangeable`)이 `order.type === 'billing'` 을 요구해 이런 발전소에서는
  // 버튼이 뜨지 않는다(빌링키가 걸린 주문이 없으니 변경할 대상 자체가 없음 — 의도된 결과).
  // 관련: docs/API-ISSUES.md — v2 발전소 목록 `include=order`.
  const order = raw.order;

  return {
    ...fromRawPlant(raw),
    isRepresentative: raw.isRepresentative,
    subscription: subscribed
      ? {
          status: STATUS_MAP[sub.subscriptionStatus],
          typeCode: TYPE_CODE_MAP[code as Exclude<RawSubscriptionCode, 'NONE'>],
          currentPeriodStartDate: sub.currentPeriodStartDate ?? '',
          currentPeriodEndDate: sub.currentPeriodEndDate ?? '',
        }
      : undefined,
    order: order
      ? {
          orderId: order.orderId,
          type: ORDER_TYPE_MAP[order.type],
          billingId: order.billingId,
          recurring: order.recurring,
          name: order.name,
          status: order.status,
          amount: order.amount,
        }
      : undefined,
  };
}

/** `GET /plants?include=subscription` 목록 응답 변환. */
export function fromRawSubscriptionList(raw: RawPlantListResponse): SubscriptionListItem[] {
  return raw.plants.map(fromRawSubscriptionListItem);
}
